import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MedicalHistoryLayout from './MedicalHistoryLayout';
import CompletedCyclesList from './CompletedCyclesList';
import FichaMedicaView from './FichaMedicaView';
import PatientRoadmap from './PatientRoadmap';
import {
    useGetPatientsQuery,
    useGetAppointmentsQuery,
    useGetMedicalHistoryQuery,
    useGetMedicalRecordsQuery,
    useUpdatePatientMutation
} from '../../services/api/kinesioApi.js';
import {
    ArrowLeft, User, Phone, Mail, Droplet, Activity,
    Calendar, Clock, FileText, ChevronRight, Stethoscope,
    CheckCircle2, ShieldPlus as Shield, ClipboardList, Image as ImageIcon,
    Plus, History, Loader2, ChevronDown, ChevronUp, ExternalLink, Pencil, Share2, X
} from 'lucide-react';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import SharePatientModal from './SharePatientModal.jsx';
import { toast } from '../ui/use-toast';

dayjs.locale('es');

const PatientProfile = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('clinica');
    const [showPreviousInProfile, setShowPreviousInProfile] = useState(false);
    const [selectedConsultationId, setSelectedConsultationId] = useState(null);
    const [selectedAppointmentId, setSelectedAppointmentId] = useState(null);
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editFormData, setEditFormData] = useState({});

    const [updatePatient, { isLoading: isUpdatingPatient }] = useUpdatePatientMutation();

    const { data: patients, isLoading: isLoadingPatients } = useGetPatientsQuery();
    const { data: allAppointments, isLoading: isLoadingAppointments } = useGetAppointmentsQuery({ patient_id: id });
    const { data: historyList = [], isLoading: isLoadingHistory } = useGetMedicalHistoryQuery(id, { skip: !id });
    const { data: records = [], isLoading: isLoadingRecords } = useGetMedicalRecordsQuery(id, { skip: !id });

    const patient = useMemo(() => {
        if (!patients || !id) return null;
        return patients.find(p => p.id === parseInt(id));
    }, [patients, id]);

    const patientAppointments = useMemo(() => {
        if (!allAppointments) return [];
        return [...allAppointments].sort((a, b) => new Date(b.fecha_hora) - new Date(a.fecha_hora));
    }, [allAppointments]);

    const completedCyclesCount = useMemo(() => {
        if (!patient) return 0;
        const cycles = patient.admissionData?.completedCycles?.length || 0;
        const isCurrentFinished = Number(patient.admissionData?.roadmapStage || 0) >= 12;
        return cycles + (isCurrentFinished ? 1 : 0);
    }, [patient]);

    const allConsultations = useMemo(() => {
        const list = [];

        // Process dynamic medical records
        if (records && records.length > 0) {
            records.forEach(r => {
                let templateFields = [];
                try {
                    templateFields = typeof r.template_snapshot === 'string'
                        ? JSON.parse(r.template_snapshot)
                        : (r.template_snapshot || []);
                } catch (e) { }

                let recordData = {};
                try {
                    recordData = typeof r.record_data === 'string'
                        ? JSON.parse(r.record_data)
                        : (r.record_data || {});
                } catch (e) { }

                list.push({
                    id: `dynamic-${r.id}`,
                    date: new Date(r.createdAt || r.updatedAt),
                    type: 'dynamic',
                    raw: r,
                    template_name: r.template_name,
                    professional_name: r.professional_name,
                    status: r.status,
                    signature_timestamp: r.signature_timestamp,
                    fields: templateFields,
                    data: recordData
                });
            });
        }

        // Process static medical history entries
        if (historyList && historyList.length > 0) {
            historyList.forEach(h => {
                list.push({
                    id: `static-${h.id}`,
                    date: new Date(h.createdAt || h.created_at || h.fecha),
                    type: 'static',
                    raw: h,
                    reason_for_visit: h.reason_for_visit,
                    blood_pressure: h.blood_pressure,
                    heart_rate: h.heart_rate,
                    physical_findings: h.physical_findings,
                    diagnostico: h.diagnostico,
                    tratamiento: h.tratamiento,
                    archivos_adjuntos: h.archivos_adjuntos || []
                });
            });
        }

        // Sort descending (newest first)
        list.sort((a, b) => b.date.getTime() - a.date.getTime());
        return list;
    }, [records, historyList]);

    const calculateAge = (dob) => {
        if (!dob) return 'N/A';
        const diff = Date.now() - new Date(dob).getTime();
        return Math.abs(new Date(diff).getUTCFullYear() - 1970);
    };

    const getInitials = (name) => {
        if (!name) return '??';
        return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
    };

    if (isLoadingPatients) return <div className="p-4 md:p-8 text-center text-gray-500">Cargando perfil del paciente...</div>;
    if (!patient) return <div className="p-4 md:p-8 text-center text-red-500">Paciente no encontrado.</div>;

    return (
        <div className="w-full min-h-full bg-[#F8FAFC] p-3 md:p-8 flex flex-col gap-5 md:gap-6 font-sans">
            {/* Header / Back Button & Actions */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-start md:items-center gap-3 md:gap-4">
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center justify-center min-w-[44px] min-h-[44px] md:p-2.5 bg-white border border-gray-200 rounded-full hover:bg-gray-50 text-gray-600 transition-colors shadow-sm shrink-0"
                    >
                        <ArrowLeft size={20} />
                    </button>
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-[#111827] leading-tight">Perfil del Paciente</h1>
                        <p className="text-gray-500 text-sm mt-1">Gestión completa de información y registros.</p>
                    </div>
                </div>
                <div className="flex items-center gap-3 w-full md:w-auto">
                    <button
                        onClick={() => {
                            setEditFormData({
                                nombre: patient.nombre || '',
                                dni: patient.dni || '',
                                fecha_nacimiento: patient.fecha_nacimiento ? patient.fecha_nacimiento.split('T')[0] : '',
                                gender: patient.gender || '',
                                blood_type: patient.blood_type || '',
                                phone: patient.datos_contacto?.phone || patient.datos_contacto?.telefono || '',
                                email: patient.datos_contacto?.email || '',
                                is_pauses: Boolean(patient.is_pauses ?? patient.admissionData?.is_pauses)
                            });
                            setIsEditModalOpen(true);
                        }}
                        className="flex-1 md:flex-none bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 px-4 py-3 md:py-2.5 rounded-xl font-bold text-sm md:text-xs transition-all shadow-xs flex items-center justify-center gap-2 min-h-[44px]"
                        title="Editar datos del paciente"
                    >
                        <Pencil size={16} /> Editar Datos
                    </button>
                    <button
                        onClick={() => setIsShareModalOpen(true)}
                        className="flex-1 md:flex-none bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-3 md:py-2.5 rounded-xl font-bold text-sm md:text-xs transition-all shadow-sm flex items-center justify-center gap-2 min-h-[44px]"
                        title="Compartir o enviar ficha e historial médico a otro profesional"
                    >
                        <Share2 size={18} className="md:w-4 md:h-4" /> Compartir Ficha / Historial
                    </button>
                </div>
            </div>

            {/* Patient Identity & Type Banner Card */}
            {(() => {
                const isPauses = Boolean(patient.is_pauses ?? patient.admissionData?.is_pauses);
                return (
            <div className="bg-white rounded-2xl border border-gray-100 p-4 md:p-6 shadow-[0_4px_20px_rgb(0,0,0,0.03)] flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    {/* Avatar del usuario con foto del logo de pauses al lado */}
                    <div className="flex items-center gap-3 shrink-0">
                        <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl flex items-center justify-center font-bold text-lg md:text-xl bg-[#EDE9FE] text-[#6D28D9] shadow-inner">
                            {getInitials(patient.nombre)}
                        </div>
                        {isPauses ? (
                            <div 
                                className="w-10 h-10 rounded-full bg-white border-2 border-emerald-400 p-0.5 shadow-xs flex items-center justify-center shrink-0 hover:scale-105 transition-transform" 
                                title="Paciente de Pauses"
                            >
                                <img 
                                    src="/images/pausesLogo.png" 
                                    alt="Pauses" 
                                    className="w-full h-full object-contain rounded-full" 
                                />
                            </div>
                        ) : (
                            <div 
                                className="w-10 h-10 rounded-full bg-slate-100 border border-slate-200 shadow-2xs flex items-center justify-center text-slate-400 shrink-0" 
                                title="Paciente de Profesional Particular"
                            >
                                <User size={18} className="text-slate-500" />
                            </div>
                        )}
                    </div>
                    <div>
                        <div className="flex items-center gap-2.5 flex-wrap">
                            <h2 className="text-xl md:text-2xl font-bold text-gray-900">{patient.nombre}</h2>
                            {isPauses ? (
                                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-[#ECFDF5] text-[#065F46] border border-[#A7F3D0] shadow-2xs">
                                    <img src="/images/pausesLogo.png" alt="Pauses" className="h-4 w-auto object-contain" />
                                    Paciente de Pauses
                                </span>
                            ) : (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200">
                                    Paciente individual
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-3 text-xs md:text-sm text-gray-500 mt-1 flex-wrap">
                            <span>ID: <strong className="text-gray-700">{patient.id}</strong></span>
                            <span>•</span>
                            <span>DNI: <strong className="text-gray-700">{patient.dni || 'Sin DNI'}</strong></span>
                            <span>•</span>
                            <span>Edad: <strong className="text-gray-700">{calculateAge(patient.fecha_nacimiento)} años</strong></span>
                            {patient.datos_contacto?.phone && (
                                <>
                                    <span>•</span>
                                    <span>Tel: <strong className="text-gray-700">{patient.datos_contacto.phone}</strong></span>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Patient Type Selector Input */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-2.5 bg-gray-50/80 border border-gray-200/80 p-2.5 sm:px-4 sm:py-3 rounded-xl shrink-0">
                    <label htmlFor="patient-type-select" className="text-xs font-bold uppercase tracking-wider text-gray-600 whitespace-nowrap">
                        Tipo de Paciente:
                    </label>
                    <div className="relative inline-flex items-center">
                        <select
                            id="patient-type-select"
                            value={isPauses ? 'pauses' : 'individual'}
                            onChange={async (e) => {
                                const newIsPauses = e.target.value === 'pauses';
                                try {
                                    await updatePatient({ 
                                        id: patient.id, 
                                        is_pauses: newIsPauses,
                                        admissionData: { ...(patient.admissionData || {}), is_pauses: newIsPauses }
                                    }).unwrap();
                                    toast({
                                        title: 'Perfil actualizado',
                                        description: `El paciente ahora está registrado como ${newIsPauses ? 'Paciente de Pauses' : 'Paciente Individual'}.`,
                                        variant: 'success'
                                    });
                                } catch (err) {
                                    toast({
                                        title: 'Error',
                                        description: 'No se pudo actualizar el tipo de paciente.',
                                        variant: 'destructive'
                                    });
                                }
                            }}
                            className={`appearance-none cursor-pointer text-xs font-bold pl-3.5 pr-8 py-2 rounded-lg border transition-all outline-none ${
                                isPauses
                                    ? 'bg-emerald-50 text-emerald-800 border-emerald-300 hover:bg-emerald-100 shadow-2xs'
                                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 shadow-2xs'
                            }`}
                        >
                            <option value="individual">Paciente individual</option>
                            <option value="pauses">Paciente de pauses</option>
                        </select>
                        <ChevronDown size={14} className="pointer-events-none absolute right-2.5 text-current opacity-60" />
                    </div>
                    {isPauses && (
                        <img 
                            src="/images/pausesLogo.png" 
                            alt="Pauses" 
                            className="h-6 w-auto object-contain hidden sm:block ml-1" 
                            title="Paciente de Pauses"
                        />
                    )}
                </div>
            </div>
                );
            })()}

            <div className="flex flex-col lg:flex-row gap-5 md:gap-6 items-start">

                {/* Left Column: Ficha Médica (Wider for better visibility) */}
                <div className="w-full lg:w-[62%] xl:w-[65%] bg-white rounded-2xl border border-gray-100 p-4 md:p-6 shadow-[0_4px_20px_rgb(0,0,0,0.03)] flex flex-col gap-5 md:gap-6">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-1 md:mb-2 border-b border-gray-200 pb-4">
                        <h2 className="text-xl font-serif text-gray-900">Ficha Médica del Paciente</h2>
                        <button className="flex items-center justify-center gap-2 text-sm md:text-xs text-gray-600 hover:text-gray-900 border border-gray-300 px-4 md:px-3 py-2 md:py-1.5 rounded-lg bg-gray-50 min-h-[44px] w-full sm:w-auto" onClick={() => window.print()}>
                            Imprimir Ficha
                        </button>
                    </div>
                    <FichaMedicaView 
                        patient={patient} 
                        legacyHistory={allConsultations} 
                        onUpdatePatient={(data) => updatePatient({ id: patient.id, ...data })}
                    />
                </div>

                {/* Right Column: Hoja de Ruta, Tabs & Content (Compacted) */}
                <div className="w-full lg:w-[38%] xl:w-[35%] flex flex-col gap-5 md:gap-4">
                    
                    <PatientRoadmap 
                        currentStage={patient.admissionData?.roadmapStage || 0}
                        roadmapNotes={patient.admissionData?.roadmapNotes || {}}
                        onCompleteStage={(newStage, notesUpdate) => {
                            updatePatient({ 
                                id: patient.id, 
                                admissionData: { 
                                    ...(patient.admissionData || {}), 
                                    roadmapStage: newStage,
                                    roadmapNotes: {
                                        ...(patient.admissionData?.roadmapNotes || {}),
                                        ...notesUpdate
                                    }
                                } 
                            });
                        }}
                        onFinishCycle={() => {
                            const currentNotes = patient.admissionData?.roadmapNotes || {};
                            let startDate = 'Desconocida';
                            let endDate = dayjs().format('DD/MM/YYYY HH:mm');
                            if (currentNotes[0] && currentNotes[0].date) {
                                startDate = dayjs(currentNotes[0].date).format('DD/MM/YYYY');
                            }

                            const newCycle = {
                                id: Date.now(),
                                startDate,
                                endDate,
                                closedAt: dayjs().format(),
                                notes: currentNotes,
                                previousStage: patient.admissionData?.roadmapStage || 0
                            };

                            const existingCycles = patient.admissionData?.completedCycles || [];

                            updatePatient({ 
                                id: patient.id, 
                                admissionData: { 
                                    ...(patient.admissionData || {}), 
                                    roadmapStage: 0,
                                    roadmapNotes: {},
                                    completedCycles: [newCycle, ...existingCycles]
                                } 
                            });
                        }}
                        hasCompletedCycles={(patient.admissionData?.completedCycles || []).length > 0}
                        onRestoreLastCycle={() => {
                            const existingCycles = patient.admissionData?.completedCycles || [];
                            if (existingCycles.length === 0) return;
                            const [lastCycle, ...remaining] = existingCycles;
                            updatePatient({ 
                                id: patient.id, 
                                admissionData: { 
                                    ...(patient.admissionData || {}), 
                                    roadmapStage: lastCycle.previousStage !== undefined ? lastCycle.previousStage : 12,
                                    roadmapNotes: lastCycle.notes || {},
                                    completedCycles: remaining
                                } 
                            });
                            toast({
                                title: "Ciclo Restaurado",
                                description: "Se ha deshecho el cierre de ciclo y restaurado el tratamiento previo.",
                                duration: 3000
                            });
                        }}
                    />

                    <div className="flex flex-col sm:flex-row bg-white rounded-xl border border-gray-100 p-1.5 shadow-sm w-full overflow-hidden gap-1 sm:gap-0">
                        <button
                            onClick={() => setActiveTab('clinica')}
                            className={`min-h-[42px] w-full sm:flex-1 px-3 py-2 rounded-lg text-xs sm:text-sm font-bold transition-all whitespace-nowrap flex items-center justify-center ${activeTab === 'clinica' ? 'bg-[#0A58CA] text-white shadow-md' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'}`}
                        >
                            Inicio y Datos
                        </button>
                        <button
                            onClick={() => setActiveTab('turnos')}
                            className={`min-h-[42px] w-full sm:flex-1 px-3 py-2 rounded-lg text-xs sm:text-sm font-bold transition-all whitespace-nowrap flex items-center justify-center ${activeTab === 'turnos' ? 'bg-[#0A58CA] text-white shadow-md' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'}`}
                        >
                            Turnos ({patientAppointments.length})
                        </button>
                        <button
                            onClick={() => setActiveTab('ciclos')}
                            className={`min-h-[42px] w-full sm:flex-1 px-3 py-2 rounded-lg text-xs sm:text-sm font-bold transition-all whitespace-nowrap flex items-center justify-center ${activeTab === 'ciclos' ? 'bg-[#0A58CA] text-white shadow-md' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'}`}
                        >
                            Ciclos ({completedCyclesCount})
                        </button>
                    </div>

                    {/* Tab Content: Turnos */}
                    {activeTab === 'turnos' && (
                        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-[0_4px_20px_rgb(0,0,0,0.03)] flex flex-col">
                            <div className="p-4 md:p-5 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-blue-50 text-blue-600 rounded-lg shrink-0"><Calendar size={20} /></div>
                                    <h3 className="font-bold text-gray-900 text-lg">Turnos Registrados ({patientAppointments.length})</h3>
                                </div>
                                <span className="text-xs text-gray-500 font-medium sm:text-right">Selecciona un turno para destacar</span>
                            </div>

                            <div className="flex flex-col divide-y divide-gray-100">
                                {isLoadingAppointments ? (
                                    <div className="p-8 text-center text-gray-500 text-sm font-medium">Cargando turnos...</div>
                                ) : patientAppointments.length === 0 ? (
                                    <div className="p-8 md:p-12 flex flex-col items-center justify-center text-center">
                                        <div className="w-16 h-16 bg-gray-50 text-gray-300 rounded-full flex items-center justify-center mb-4">
                                            <Calendar size={32} />
                                        </div>
                                        <p className="text-gray-500 font-medium">Este paciente aún no tiene turnos registrados.</p>
                                    </div>
                                ) : (
                                    patientAppointments.map(appt => {
                                        const date = dayjs(appt.fecha_hora);
                                        const isPast = date.isBefore(dayjs());
                                        const isSelected = selectedAppointmentId === appt.id;

                                        return (
                                            <div
                                                key={appt.id}
                                                onClick={() => setSelectedAppointmentId(isSelected ? null : appt.id)}
                                                className={`p-4 md:p-5 transition-all cursor-pointer flex items-center justify-between gap-2 md:gap-4 min-h-[64px] ${isSelected ? 'bg-blue-50/80 border-l-4 border-l-[#0A58CA]' : 'hover:bg-gray-50/50'
                                                    }`}
                                            >
                                                <div className="flex items-start sm:items-center gap-3 md:gap-4 w-full min-w-0">
                                                    <div className={`w-12 h-12 rounded-xl flex flex-col items-center justify-center border shrink-0 ${isSelected ? 'bg-[#0A58CA] text-white border-[#0A58CA]' : isPast ? 'bg-gray-50 border-gray-200 text-gray-500' : 'bg-blue-50 border-blue-100 text-blue-700'
                                                        }`}>
                                                        <span className="text-xs font-bold uppercase">{date.format('MMM')}</span>
                                                        <span className="text-lg font-black leading-none">{date.format('DD')}</span>
                                                    </div>
                                                    <div className="flex flex-col flex-1 min-w-0">
                                                        <div className="flex flex-wrap items-center gap-2">
                                                            <h4 className="font-bold text-gray-900 text-sm md:text-base truncate">{date.format('dddd, DD MMMM YYYY')}</h4>
                                                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide whitespace-nowrap ${appt.estado === 'completado' ? 'bg-green-100 text-green-700' : appt.estado === 'cancelado' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
                                                                }`}>
                                                                {appt.estado || 'Pendiente'}
                                                            </span>
                                                        </div>
                                                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 mt-1">
                                                            <span className="text-xs md:text-sm font-medium text-gray-500 flex items-center gap-1.5">
                                                                <Clock size={14} className="shrink-0" /> {date.format('HH:mm')} hs
                                                            </span>
                                                            {appt.motivo && (
                                                                <span className="text-xs md:text-sm text-gray-500 flex items-start sm:items-center gap-1.5 sm:border-l sm:border-gray-200 sm:pl-4">
                                                                    <Stethoscope size={14} className="shrink-0 mt-0.5 sm:mt-0" /> <span className="line-clamp-2 sm:line-clamp-1">{appt.motivo}</span>
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                                <ChevronRight size={18} className={`transition-transform shrink-0 ${isSelected ? 'text-[#0A58CA] translate-x-1' : 'text-gray-300'}`} />
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    )}

                    {/* Tab Content: Inicio y Datos (antes Historia Clinica) */}
                    {activeTab === 'clinica' && (
                        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-2">
                           <MedicalHistoryLayout patient={patient} legacyHistory={allConsultations} />
                        </div>
                    )}

                    {/* Tab Content: Ciclos Terminados */}
                    {activeTab === 'ciclos' && (
                        <div className="bg-white rounded-2xl border border-gray-100 shadow-[0_4px_20px_rgb(0,0,0,0.03)] p-4 md:p-6 min-h-[400px] md:min-h-[500px]">
                            {patient.admissionData?.completedCycles?.length > 0 ? (
                                <CompletedCyclesList 
                                    cycles={patient.admissionData.completedCycles} 
                                    onUndo={(cycleId) => {
                                        const cycles = patient.admissionData.completedCycles || [];
                                        const cycleToRestore = cycles.find(c => c.id === cycleId);
                                        const remainingCycles = cycles.filter(c => c.id !== cycleId);
                                        if (cycleToRestore) {
                                            updatePatient({
                                                id: patient.id,
                                                admissionData: {
                                                    ...(patient.admissionData || {}),
                                                    roadmapStage: cycleToRestore.previousStage || 12,
                                                    roadmapNotes: cycleToRestore.notes || {},
                                                    completedCycles: remainingCycles
                                                }
                                            });
                                        }
                                    }}
                                />
                            ) : (
                                <div className="h-full flex flex-col items-center justify-center py-12 md:py-20">
                                    <div className="w-16 h-16 bg-green-50 text-green-300 rounded-full flex items-center justify-center mb-4">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-file-check"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/><path d="M14 2v4a2 2 0 0 0 2 2h4"/><path d="m9 15 2 2 4-4"/></svg>
                                    </div>
                                    <p className="text-gray-500 font-medium text-center max-w-sm text-sm md:text-base">Este paciente aún no ha completado ningún ciclo de tratamiento de 3 meses.</p>
                                </div>
                            )}
                        </div>
                    )}



                </div>
            </div>

            {/* Share Patient Modal */}
            <SharePatientModal
                patient={patient}
                isOpen={isShareModalOpen}
                onClose={() => setIsShareModalOpen(false)}
            />

            {/* Edit Patient Modal */}
            {isEditModalOpen && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]">
                        <div className="flex justify-between items-center p-4 border-b border-gray-100 bg-gray-50">
                            <h3 className="font-bold text-lg text-gray-900">Editar Información del Paciente</h3>
                            <button onClick={() => setIsEditModalOpen(false)} className="text-gray-400 hover:text-gray-600 min-w-[44px] min-h-[44px] flex items-center justify-center">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="p-4 md:p-6 flex-1 overflow-y-auto">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="col-span-1 md:col-span-2">
                                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Nombre Completo</label>
                                    <input
                                        type="text"
                                        className="w-full border border-gray-200 rounded-lg p-3 md:p-2.5 text-base md:text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none min-h-[44px]"
                                        value={editFormData.nombre}
                                        onChange={(e) => setEditFormData({ ...editFormData, nombre: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">DNI</label>
                                    <input
                                        type="text"
                                        className="w-full border border-gray-200 rounded-lg p-3 md:p-2.5 text-base md:text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none min-h-[44px]"
                                        value={editFormData.dni}
                                        onChange={(e) => setEditFormData({ ...editFormData, dni: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Fecha Nacimiento</label>
                                    <input
                                        type="date"
                                        className="w-full border border-gray-200 rounded-lg p-3 md:p-2.5 text-base md:text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none min-h-[44px]"
                                        value={editFormData.fecha_nacimiento}
                                        onChange={(e) => setEditFormData({ ...editFormData, fecha_nacimiento: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Género</label>
                                    <select
                                        className="w-full border border-gray-200 rounded-lg p-3 md:p-2.5 text-base md:text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none min-h-[44px] bg-white"
                                        value={editFormData.gender}
                                        onChange={(e) => setEditFormData({ ...editFormData, gender: e.target.value })}
                                    >
                                        <option value="">Seleccionar</option>
                                        <option value="Masculino">Masculino</option>
                                        <option value="Femenino">Femenino</option>
                                        <option value="Otro">Otro</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Grupo Sanguíneo</label>
                                    <select
                                        className="w-full border border-gray-200 rounded-lg p-3 md:p-2.5 text-base md:text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none min-h-[44px] bg-white"
                                        value={editFormData.blood_type}
                                        onChange={(e) => setEditFormData({ ...editFormData, blood_type: e.target.value })}
                                    >
                                        <option value="">Seleccionar</option>
                                        <option value="A+">A+</option><option value="A-">A-</option>
                                        <option value="B+">B+</option><option value="B-">B-</option>
                                        <option value="AB+">AB+</option><option value="AB-">AB-</option>
                                        <option value="O+">O+</option><option value="O-">O-</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Teléfono</label>
                                    <input
                                        type="tel"
                                        className="w-full border border-gray-200 rounded-lg p-3 md:p-2.5 text-base md:text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none min-h-[44px]"
                                        value={editFormData.phone}
                                        onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Email</label>
                                    <input
                                        type="email"
                                        className="w-full border border-gray-200 rounded-lg p-3 md:p-2.5 text-base md:text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none min-h-[44px]"
                                        value={editFormData.email}
                                        onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Tipo de Paciente</label>
                                    <select
                                        className="w-full border border-gray-200 rounded-lg p-3 md:p-2.5 text-base md:text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none min-h-[44px] bg-white"
                                        value={editFormData.is_pauses ? 'pauses' : 'individual'}
                                        onChange={(e) => setEditFormData({ ...editFormData, is_pauses: e.target.value === 'pauses' })}
                                    >
                                        <option value="individual">Paciente individual</option>
                                        <option value="pauses">Paciente de pauses</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                        <div className="p-4 border-t border-gray-100 bg-gray-50 flex flex-col-reverse sm:flex-row justify-end gap-3">
                            <button
                                onClick={() => setIsEditModalOpen(false)}
                                className="w-full sm:w-auto px-5 py-3 md:py-2.5 rounded-lg text-gray-600 font-bold hover:bg-gray-200 transition-colors text-sm min-h-[44px]"
                            >
                                Cancelar
                            </button>
                            <button
                                disabled={isUpdatingPatient || !editFormData.nombre.trim()}
                                onClick={async () => {
                                    try {
                                        await updatePatient({
                                            id: patient.id,
                                            nombre: editFormData.nombre,
                                            dni: editFormData.dni,
                                            fecha_nacimiento: editFormData.fecha_nacimiento || null,
                                            gender: editFormData.gender,
                                            blood_type: editFormData.blood_type,
                                            is_pauses: Boolean(editFormData.is_pauses),
                                            admissionData: {
                                                ...(patient.admissionData || {}),
                                                is_pauses: Boolean(editFormData.is_pauses)
                                            },
                                            datos_contacto: {
                                                phone: editFormData.phone,
                                                telefono: editFormData.phone,
                                                email: editFormData.email
                                            }
                                        }).unwrap();
                                        setIsEditModalOpen(false);
                                    } catch (e) {
                                        console.error(e);
                                    }
                                }}
                                className="w-full sm:w-auto px-5 py-3 md:py-2.5 rounded-lg bg-[#0A58CA] hover:bg-blue-700 text-white font-bold transition-colors shadow-sm text-sm disabled:opacity-50 flex items-center justify-center gap-2 min-h-[44px]"
                            >
                                {isUpdatingPatient ? <Loader2 size={16} className="animate-spin" /> : 'Guardar Cambios'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PatientProfile;
